"use client";

import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { useDataContext } from "@/app/_context";
import { usePathname } from "next/navigation";

export default function AppContentFrame({ children }: { children: ReactNode }) {
  const { scrollPositionY, setScrollPositionY } = useDataContext();
  const pathname = usePathname();
  const prevScrollY = useRef(scrollPositionY);
  const [scrollingDown, setScrollingDown] = useState(false);

  useLayoutEffect(() => {
    prevScrollY.current = 0;
    setScrollingDown(false);
    setScrollPositionY(0);
  }, [pathname, setScrollPositionY]);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      if (scrollPositionY < 60) {
        setScrollingDown(false);
      } else if (scrollPositionY > prevScrollY.current) {
        setScrollingDown(true);
      } else if (scrollPositionY < prevScrollY.current) {
        setScrollingDown(false);
      }

      prevScrollY.current = scrollPositionY;
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [scrollPositionY]);

  const hasOwnChrome =
    pathname.includes("widget") ||
    pathname.includes("/admin") ||
    pathname.includes("/preview") ||
    pathname.includes("/public");
  const isHomePage = pathname === "/";
  const isBlogRoute = pathname.includes("/blog");
  const navHidden = isBlogRoute && (scrollPositionY < 60 || scrollingDown);

  return (
    <div
      className={cn(
        "relative w-full min-h-0 transition-[height,margin] duration-500 ease-in-out",
        hasOwnChrome
          ? "h-full"
          : navHidden
          ? "mt-0 h-[100dvh]"
          : "mt-14 h-[calc(100dvh-3.5rem)] md:mt-20 md:h-[calc(100dvh-5rem)]",
      )}
    >
      <div className={cn("min-h-0", isHomePage ? "h-full overflow-y-auto" : "h-full *:h-full *:min-h-0")}>
        {children}
      </div>
    </div>
  );
}
